import * as React from 'react';

import { constantFormat } from '../../../helpers/constants';
import { getPDFNoteStatusBadge, getPDFNoteStatusDisplayName } from '../../../helpers/mapping/PDFNoteStatusVals';
import { NoteCardActions } from './NoteCardActions';
import { PDFNote } from '../models/PDFNote';
import { PDFNoteWorkflowInstance } from '../models/PDFNoteWorkflowInstance';
import { ProjectDoc } from '../../projectDocs/models/ProjectDoc';
import { RemarkCard, RemarkPillSpec } from '../../../components/synced/ui/RemarkCard/RemarkCard';
import { ImageLightbox } from '../../../components/synced/widgets/PDFViewer/components/ImageLightbox';
import { NoteUpdatesController } from '../helpers/noteUpdates';

interface NoteCardProps {
    pdfNote: PDFNote;
    projectDocFolderStatusId: number;
    projectDoc?: ProjectDoc;
    onNoteChanged: () => void;
    onFormLinkClick?: () => void;
    defaultExpanded?: boolean;
    noteUpdates?: NoteUpdatesController;
    noteUpdatesPending?: boolean;
}

const statusPill = (statusId?: number): RemarkPillSpec => {
    const isSuccess = getPDFNoteStatusBadge(statusId) === `badge badge-success`;
    return isSuccess
        ? {
              label: getPDFNoteStatusDisplayName(statusId),
              bg: `var(--success-bg)`,
              color: `var(--success-color)`,
              dot: `var(--success-color)`
          }
        : {
              label: getPDFNoteStatusDisplayName(statusId),
              bg: `var(--status-draft-bg)`,
              color: `var(--status-draft-color)`,
              dot: `var(--status-draft-color)`
          };
};

const categoryPill = (pdfNote: PDFNote): RemarkPillSpec | undefined => {
    if (!pdfNote.Code) return undefined;
    const label = `${pdfNote.Code.Id} — ${pdfNote.Code.Title}`;
    return pdfNote.IsCritical
        ? { label, bg: `var(--cat-red-bg)`, color: `var(--cat-red-color)`, dot: `var(--cat-red-color)` }
        : { label, bg: `var(--status-draft-bg)`, color: `var(--text-tertiary)`, dot: `var(--text-caret)` };
};

export const NoteCard: React.FC<NoteCardProps> = ({
    pdfNote,
    projectDocFolderStatusId,
    projectDoc,
    onNoteChanged,
    onFormLinkClick,
    defaultExpanded = false,
    noteUpdates,
    noteUpdatesPending
}) => {
    const [expanded, setExpanded] = React.useState<boolean>(defaultExpanded);
    const [showMore, setShowMore] = React.useState<boolean>(true);
    const [lightboxSrc, setLightboxSrc] = React.useState<string | null>(null);

    const divRef = React.useRef<HTMLDivElement>(null);
    const onToggle = (e) => {
        e.preventDefault();
        setExpanded((prev) => !prev);
    };

    React.useEffect(() => {
        if (!divRef.current) return;
        if (divRef.current.scrollHeight >= 100 || divRef.current.getElementsByTagName('img').length > 0) return;
        setShowMore(false);
    }, []);

    React.useEffect(() => {
        const el = divRef.current;
        if (!el) return;
        const handler = (e: MouseEvent) => {
            const target = e.target as HTMLElement;
            if (target.tagName === 'IMG') {
                e.preventDefault();
                e.stopPropagation();
                setLightboxSrc((target as HTMLImageElement).src);
            }
        };
        el.addEventListener('click', handler);
        return () => el.removeEventListener('click', handler);
    }, [pdfNote.Title]);

    const [expandedComment, setExpandedComment] = React.useState<boolean>(defaultExpanded);
    const [showMoreComment, setShowMoreComment] = React.useState<boolean>(true);

    const divRefComment = React.useRef<HTMLDivElement>(null);
    const onToggleComment = (e) => {
        e.preventDefault();
        setExpandedComment((prev) => !prev);
    };

    React.useEffect(() => {
        if (!divRefComment.current) return;
        if (divRefComment.current.scrollHeight >= 100 || divRefComment.current.getElementsByTagName('img').length > 0)
            return;
        setShowMoreComment(false);
    }, []);

    const onFormClick = (e) => {
        if (onFormLinkClick) onFormLinkClick();
        e.preventDefault();
    };

    const contextInstance: PDFNoteWorkflowInstance | null = React.useMemo<PDFNoteWorkflowInstance | null>(() => {
        const instances: PDFNoteWorkflowInstance[] = [...pdfNote.PDFNoteWorkflowInstances].sort(
            (i1, i2) => i1.WorkflowInstanceId - i2.WorkflowInstanceId
        );
        return instances[instances.length - 1];
        // eslint-disable-next-line
    }, [pdfNote]);

    const body = (
        <React.Fragment>
            <div
                ref={divRef}
                className={`note-body ${expanded ? `note-expanded` : ``}`}
                dangerouslySetInnerHTML={{ __html: pdfNote?.Title ?? `` }}
            />
            {showMore && (
                <button type="button" className="note-expand-btn" onClick={onToggle}>
                    <small className="text-muted">{expanded ? `Свернуть` : `Развернуть`}</small>
                </button>
            )}
        </React.Fragment>
    );

    const reply =
        contextInstance && contextInstance.CompletionComment ? (
            <div className="ka-reply">
                <div className="ka-reply-header">Ответ КА</div>
                <div className="ka-reply-body">
                    <div
                        ref={divRefComment}
                        className={`note-body ${expandedComment ? `note-expanded` : ``}`}
                        dangerouslySetInnerHTML={{ __html: contextInstance.CompletionComment ?? `` }}
                    />
                    {showMoreComment && (
                        <button type="button" className="note-expand-btn" onClick={onToggleComment}>
                            <small className="text-muted">{expandedComment ? `Свернуть` : `Развернуть`}</small>
                        </button>
                    )}
                </div>
            </div>
        ) : undefined;

    return (
        <>
            <RemarkCard
                className="ka-remark"
                onFormClick={onFormClick}
                code={pdfNote.Id}
                category={categoryPill(pdfNote)}
                sheet={pdfNote?.PageInfo || undefined}
                status={statusPill(pdfNote?.StatusId)}
                body={body}
                replySlot={reply}
                actionsSlot={
                    <NoteCardActions
                        pdfNote={pdfNote}
                        projectDocFolderStatusId={projectDocFolderStatusId}
                        projectDoc={projectDoc}
                        onNoteChanged={onNoteChanged}
                        {...(noteUpdates ? { noteUpdates, noteUpdatesPending } : {})}
                    />
                }
                date={constantFormat.DateTimeToString(pdfNote?.Created)}
                author={pdfNote?.CreatedBy?.Title}
                commentsCount={pdfNote.Comments.filter((x) => x.NotForContractor === false).length ?? 0}
            />
            {lightboxSrc && <ImageLightbox src={lightboxSrc} onClose={() => setLightboxSrc(null)} />}
        </>
    );
};
